import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { buildApiUrl, API_CONFIG } from '../../config/api';

type Estado = 'pendiente' | 'aceptado' | 'rechazado';

interface PendienteItem {
  envio_id: number;
  iddocumento: number;
  referenciaexterna: string;
  id_cicla?: number | null;
  tipo?: string | null;
  referenciainterna?: string | null;
  consecutivo?: string | null;
  clave_numerica?: string | null;
  estado_aceptacion: Estado;
  fecha_envio?: string | null;
  fecha_consulta?: string | null;
}

interface PendientesResponse {
  success: boolean;
  data: PendienteItem[];
  total: number;
  page: number;
  per_page: number;
}

interface ActualizarLoteResponse {
  success: boolean;
  resultados: Array<{
    envio_id: number;
    iddocumento?: number | null;
    consecutivo?: string | null;
    clave_numerica?: string | null;
    estado_aceptacion: Estado;
    ok: boolean;
    message: string;
  }>;
}

const BATCH_SIZE = 20;
const TIMEOUT_MS = 120000;

const getStatusColor = (status: Estado) => {
  switch (status) {
    case 'aceptado':
      return 'bg-green-100 text-green-800';
    case 'rechazado':
      return 'bg-red-100 text-red-800';
    case 'pendiente':
    default:
      return 'bg-yellow-100 text-yellow-800';
  }
};

const formatDate = (dateString?: string | null) => {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleString('es-ES');
};

const PendientesSicla: React.FC = () => {
  const [items, setItems] = useState<PendienteItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updating, setUpdating] = useState(false);
  const [processed, setProcessed] = useState(0);
  const [totalToProcess, setTotalToProcess] = useState(0);
  const [lastMessages, setLastMessages] = useState<Record<number, string>>({});

  const pendientesIds = useMemo(
    () => items.map((i) => i.envio_id),
    [items]
  );

  const loadPendientes = async () => {
    setError('');
    setLoading(true);
    try {
      const res = await axios.get<PendientesResponse>(
        buildApiUrl(API_CONFIG.CICLA.PENDIENTES_LIST)
      );
      setItems(res.data.data || []);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error al cargar pendientes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPendientes();
  }, []);

  const handleActualizar = async () => {
    setError('');
    const ids = pendientesIds;
    if (ids.length === 0) return;

    setUpdating(true);
    setProcessed(0);
    setTotalToProcess(ids.length);
    setLastMessages({});

    let processedCount = 0;

    for (let i = 0; i < ids.length; i += BATCH_SIZE) {
      const chunk = ids.slice(i, i + BATCH_SIZE);
      try {
        const res = await axios.post<ActualizarLoteResponse>(
          buildApiUrl(API_CONFIG.CICLA.PENDIENTES_ACTUALIZAR_LOTE),
          { envio_ids: chunk },
          { timeout: TIMEOUT_MS }
        );

        const resultados = res.data.resultados || [];

        // Actualizar filas con los datos retornados
        setItems((prev) =>
          prev
            .map((row) => {
              const r = resultados.find((x) => x.envio_id === row.envio_id);
              if (!r) return row;
              return {
                ...row,
                consecutivo: r.consecutivo ?? row.consecutivo,
                clave_numerica: r.clave_numerica ?? row.clave_numerica,
                estado_aceptacion: r.estado_aceptacion,
              };
            })
            // remover los que ya no están pendientes
            .filter((row) => row.estado_aceptacion === 'pendiente')
        );

        setLastMessages((prev) => {
          const next = { ...prev };
          for (const r of resultados) next[r.envio_id] = r.message;
          return next;
        });
      } catch (err: any) {
        const msg =
          err.response?.data?.detail || err.message || 'Error al actualizar lote';
        setLastMessages((prev) => {
          const next = { ...prev };
          for (const eid of chunk) next[eid] = msg;
          return next;
        });
      }

      processedCount += chunk.length;
      setProcessed(processedCount);
    }

    setUpdating(false);
    // recargar para reflejar cualquier cambio adicional
    await loadPendientes();
  };

  const progressPercent =
    totalToProcess > 0 ? Math.round((processed / totalToProcess) * 100) : 0;

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Pendientes SICLA
            </h1>
            <p className="mt-1 text-gray-600">
              Documentos con estado pendiente (facturas y notas de crédito)
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={loadPendientes}
              disabled={loading || updating}
              className="px-4 py-2 rounded bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
            >
              Recargar
            </button>
            <button
              onClick={handleActualizar}
              disabled={loading || updating || items.length === 0}
              className="px-4 py-2 rounded bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {updating ? 'Actualizando...' : 'Actualizar'}
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded">
            {error}
          </div>
        )}

        {updating && (
          <div className="mb-4">
            <div className="flex justify-between text-sm text-gray-600 mb-1">
              <span>
                Procesando... {processed} de {totalToProcess}
              </span>
              <span>{progressPercent}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-primary-600 h-3 rounded-full transition-all duration-300"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>
        )}

        <div className="bg-white shadow rounded-lg overflow-hidden">
          {loading ? (
            <div className="p-6 text-gray-600">Cargando...</div>
          ) : items.length === 0 ? (
            <div className="p-6 text-gray-600">
              No hay documentos pendientes.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left">Tipo</th>
                    <th className="px-3 py-2 text-left">Ref. externa</th>
                    <th className="px-3 py-2 text-left">Ref. interna</th>
                    <th className="px-3 py-2 text-left">Consecutivo</th>
                    <th className="px-3 py-2 text-left">Clave</th>
                    <th className="px-3 py-2 text-left">Estado</th>
                    <th className="px-3 py-2 text-left">Enviado</th>
                    <th className="px-3 py-2 text-left">Últ. consulta</th>
                    <th className="px-3 py-2 text-left">Mensaje</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {items.map((row) => (
                    <tr key={row.envio_id} className="hover:bg-gray-50">
                      <td className="px-3 py-2">{row.tipo || '—'}</td>
                      <td className="px-3 py-2">{row.referenciaexterna}</td>
                      <td className="px-3 py-2 font-mono text-xs">
                        {row.referenciainterna || '—'}
                      </td>
                      <td className="px-3 py-2">{row.consecutivo || '—'}</td>
                      <td className="px-3 py-2 font-mono text-xs">
                        {row.clave_numerica || '—'}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                            row.estado_aceptacion
                          )}`}
                        >
                          {row.estado_aceptacion}
                        </span>
                      </td>
                      <td className="px-3 py-2">{formatDate(row.fecha_envio)}</td>
                      <td className="px-3 py-2">
                        {formatDate(row.fecha_consulta)}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {lastMessages[row.envio_id] || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PendientesSicla;

